import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import UpdateCCPopUp from './index';
import usePowerSubscriptionId from '../../hooks/usePowerSubscriptionId';
import useSubscriptionRecovery from '../../hooks/useSubscriptionRecovery';
import { closePopup } from '../../../actions/popup-actions';
import { showSuccessPopup, showInfoPopup } from '../../../actions/notification-actions';
import { PopupTypes } from '../../../utils/constants';
import { SubscriptionGateways } from '../../Analytics/constants';

// Bounds the caller-supplied deferred action the same way the Power upsell does, so a slow onSuccess
// can never keep the flow hanging.
const ON_SUCCESS_TIMEOUT_MS = 5000;
// After the card update the member flips back to active only once Stripe delivers the
// customer.subscription.updated webhook (which reactivates Power in the DB and clears the cache).
// auth/current re-derives past-due from a fresh DB read, so it reflects as soon as that lands — poll a
// short window past the Subscriptions-page 3 x 2s default to cover normal webhook lag; it early-exits
// the moment recovery confirms.
const RECOVERY_MAX_RETRIES = 5;
const RECOVERY_RETRY_DELAY_MS = 2000;

// Redux-driven wrapper around the shared Update-Credit-Card modal (TMS-2338 deliverable 4). Opened via
// the popup system (bookmark past-due flow, profile lapsed Resume CTA) with a deferred `onSuccess`
// stashed at `updateCcOnSuccess`. Callers stay dumb: this container self-resolves the Power
// subscription id (deliverable 7), and once the card is updated it polls for the member to be back on
// active Power (deliverable 3) — showing the Update button's spinner + disabled inputs during the poll,
// not the skeleton (which is only for the initial id resolution). Once recovery confirms, it shows the
// restored-subscription confirmation and, on OK, runs the deferred action (opening the Save-to-List
// modal). On a recovery timeout it leaves the member where they were and never runs the deferred action.
const UpdateCCPopupContainer = () => {
    const dispatch = useDispatch();
    const deferredOnSuccess = useSelector(({ popups }) => popups?.[`${PopupTypes.UPDATE_CC_POPUP}OnSuccess`]);
    const isResume = useSelector(({ auth }) => !!auth?.user?.hasPastDueSubscription);
    // The summary needs the full power subscription (price / renewal) plus the member's billing
    // interval + gateway — the same data the modal already loads to resolve the subscription id.
    const powerSubscription = useSelector(({ subscribe }) => subscribe?.subscriptions?.find((sub) => !sub.clubId));
    const subscriptionTierType = useSelector(({ auth }) => auth?.user?.subscriptionTierType);
    const subscriptionGateway = useSelector(({ auth }) => auth?.user?.subscriptionGateway);
    const { subscriptionId, isResolving } = usePowerSubscriptionId();
    const { isRecovering, runRecovery } = useSubscriptionRecovery();
    const isMonthly = subscriptionTierType === 'month';
    const isAppleSubscriber = subscriptionGateway === SubscriptionGateways.APPLE;

    const close = () => dispatch(closePopup(PopupTypes.UPDATE_CC_POPUP));

    const runDeferredAction = async () => {
        if (!deferredOnSuccess) {
            return;
        }
        let timeoutId;
        try {
            const timeoutPromise = new Promise((_, reject) => {
                timeoutId = setTimeout(() => reject(new Error('onSuccess timed out')), ON_SUCCESS_TIMEOUT_MS);
            });
            await Promise.race([Promise.resolve(deferredOnSuccess()), timeoutPromise]);
        } catch {
            // Best-effort: the card was updated and Power restored, so a failure or timeout of the
            // deferred UI action (opening the save modal) is non-critical.
        } finally {
            clearTimeout(timeoutId);
        }
    };

    const handleCardComplete = async () => {
        const recovered = await runRecovery({
            maxRetries: RECOVERY_MAX_RETRIES,
            retryDelayMs: RECOVERY_RETRY_DELAY_MS,
        });
        close();
        if (recovered) {
            dispatch(showSuccessPopup({
                title: 'Subscription Restored!',
                message: 'Your payment was successful and your Power benefits have been restored.',
                confirmButtonLabel: 'OK',
                onConfirmClick: () => { runDeferredAction(); },
            }));
        } else {
            dispatch(showInfoPopup({
                title: 'Payment Method Updated',
                message: 'Your payment method has been updated successfully. If your Power benefits aren\'t back yet, please sign out and back in.',
                confirmButtonLabel: 'OK',
            }));
        }
    };

    return (
        <UpdateCCPopUp
            onCloseClick={close}
            onCancel={close}
            onSuccess={handleCardComplete}
            subscriptionId={subscriptionId}
            forceSubmitting={isRecovering}
            isResume={isResume}
            isResolvingSubscription={isResolving}
            showSubscriptionSummary
            subscription={powerSubscription}
            isMonthly={isMonthly}
            isAppleSubscriber={isAppleSubscriber}
        />
    );
};

export default UpdateCCPopupContainer;
